import { Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { LoginModel } from "@shared/interfaces/login.model";
import { EMPTY } from "rxjs";
import { catchError, map } from "rxjs/operators";

import {
  GET_LOGIN_ALERT,
  GET_SECRET_QUESTION_LIST,
  RESET_CONCURRENT_LOGIN_ERROR,
  SAVE_USER_LOGIN_VALIDATION,
  SET_CONCURRENT_LOGIN_ERROR,
  SET_IE_NOTIFICATION,
  SET_LOGIN_STATE,
  SUBMIT_RESET_PASSWORD,
  TESTING_EXPIRE,
} from "./login.action";
import { LoginService } from "./login.service";

@State<LoginModel>({
  name: "LoginState",
  defaults: {
    isLoggedIn: false,
    secretQuestionList: null,
    isFirstTimeLoginSuccess: false,
    firstTimeLoginErrorMsg: null,
    tempUserId: null,
    tempPass: null,
    alertMsg: null,
    isResetPasswordSuccess: false,
    resetPasswordErrorMsg: null,
    resetPasswordErrorTitle: null,
    concurrentLoginState: {
      isConcurrentLogin: false,
      concurrentLoginErrorMsg: null,
      concurrentLoginErrorInfo: null,
    },
    isShowIENotification: true,
  },
})
@Injectable()
export class LoginState {
  constructor(
    private readonly loginService: LoginService,
    private readonly store: Store,
    private readonly router: Router,
    private readonly ngZone: NgZone,
  ) {}

  @Selector()
  public static getLoggedInState(state: LoginModel) {
    return state.isLoggedIn;
  }
  @Selector()
  public static getSecretQuestionList(state: LoginModel) {
    return state.secretQuestionList;
  }
  @Selector()
  public static getAlertMsg(state: LoginModel) {
    return state.alertMsg;
  }

  @Selector()
  public static concurrentLoginError(state: LoginModel) {
    return state.concurrentLoginState;
  }

  @Action(SET_LOGIN_STATE)
  public setLoginState(
    { patchState }: StateContext<LoginModel>,
    { payload }: SAVE_USER_LOGIN_VALIDATION,
  ) {
    patchState({
      isLoggedIn: payload,
    });
  }
  @Action(SAVE_USER_LOGIN_VALIDATION)
  public saveUserValidation(
    { patchState }: StateContext<LoginModel>,
    { payload }: SAVE_USER_LOGIN_VALIDATION,
  ) {
    patchState({
      tempUserId: payload.tempUserId,
      tempPass: payload.tempPass,
    });
  }

  @Action(GET_SECRET_QUESTION_LIST)
  public getSecretQuestionList({ patchState }: StateContext<LoginModel>) {
    return this.loginService.getQuestionList().pipe(
      map((res) => {
        if (res) {
          patchState({
            secretQuestionList: res.data.secretQuestions,
          });
        }
      }),
    );
  }

  @Action(GET_LOGIN_ALERT)
  public getLoginAlert({ patchState }: StateContext<LoginModel>) {
    return this.loginService.getLoginAlertMsg().pipe(
      map((res) => {
        if (res) {
          patchState({
            alertMsg: res.message ? res.message : "",
          });
        } else {
          patchState({
            alertMsg: null,
          });
        }
      }),
    );
  }
  @Action(RESET_CONCURRENT_LOGIN_ERROR)
  public resetConcurrentLoginError({ patchState }: StateContext<LoginModel>) {
    patchState({
      concurrentLoginState: {
        isConcurrentLogin: false,
        concurrentLoginErrorMsg: null,
        concurrentLoginErrorInfo: null,
      },
    });
  }
  @Action(SET_CONCURRENT_LOGIN_ERROR)
  public setConcurrentLoginError(
    { patchState }: StateContext<LoginModel>,
    { payload }: SET_CONCURRENT_LOGIN_ERROR,
  ) {
    patchState({
      concurrentLoginState: {
        isConcurrentLogin: true,
        concurrentLoginErrorMsg: payload.message,
        concurrentLoginErrorInfo: payload.info,
      },
    });
    this.ngZone.run(() => this.router.navigate(["/login"]));
  }
  @Action(SUBMIT_RESET_PASSWORD)
  public submitResetPassword(
    { patchState }: StateContext<LoginModel>,
    { payload }: SUBMIT_RESET_PASSWORD,
  ) {
    return this.loginService.submitResetPassword(payload).pipe(
      map((res) => {
        if (res.success == true) {
          patchState({
            isResetPasswordSuccess: true,
            resetPasswordErrorMsg: null,
            resetPasswordErrorTitle: null,
          });
        } else {
          patchState({
            isResetPasswordSuccess: false,
            resetPasswordErrorMsg: res.message,
            resetPasswordErrorTitle: res.error,
          });
        }
      }),
      catchError((err: Error) => {
        patchState({
          isResetPasswordSuccess: false,
          resetPasswordErrorMsg: err?.message ?? null,
          resetPasswordErrorTitle: err?.name ?? null,
        });
        return EMPTY;
      }),
    );
  }

  @Action(TESTING_EXPIRE)
  public testExpireSession({ patchState }: StateContext<LoginModel>) {
    return this.loginService.testExpireSession().pipe(
      map((res) => {
        if (res.success == true) {
          patchState({
            isResetPasswordSuccess: true,
            resetPasswordErrorMsg: null,
          });
        } else {
          patchState({
            isResetPasswordSuccess: false,
            resetPasswordErrorMsg: res.error,
          });
        }
      }),
      catchError((err: Error) => {
        patchState({
          isResetPasswordSuccess: false,
          resetPasswordErrorMsg: err?.message ?? null,
        });
        return EMPTY;
      }),
    );
  }

  @Action(SET_IE_NOTIFICATION)
  public setIENotification(
    { patchState }: StateContext<LoginModel>,
    { payload }: SET_IE_NOTIFICATION,
  ) {
    patchState({
      isShowIENotification: payload,
    });
  }
}
